/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
    AccountInfo,
    EventType,
    InteractionRequiredAuthError,
    PublicClientApplication,
    SilentRequest,
} from '@azure/msal-browser'

// Use Vite environment variables
const AUTH_B2C_POLICIES_NAMES_SIGN_UP_SIGN_IN = import.meta.env.VITE_AUTH_B2C_POLICIES_NAMES_SIGN_UP_SIGN_IN as string

const AUTH_B2C_POLICIES_AUTHORITY_SIGN_UP_SIGN_IN = import.meta.env
    .VITE_AUTH_B2C_POLICIES_AUTHORITY_SIGN_UP_SIGN_IN as string

const AUTH_B2C_POLICIES_AUTHORITY_DOMAIN = import.meta.env.VITE_AUTH_B2C_POLICIES_AUTHORITY_DOMAIN as string

const AUTH_CLIENT_ID = import.meta.env.VITE_AUTH_CLIENT_ID as string

const AUTH_REDIRECT_URL = import.meta.env.VITE_AUTH_REDIRECT_URL as string

const AUTH_SCOPE = import.meta.env.VITE_AUTH_SCOPE as string
const SPEC_SHEET_SCOPE = import.meta.env.VITE_SPEC_SHEET_SCOPE as string

/**
 * Enter here the user flows and custom policies for your B2C application
 * To learn more about user flows, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/user-flow-overview
 * To learn more about custom policies, visit: https://docs.microsoft.com/en-us/azure/active-directory-b2c/custom-policy-overview
 */
const b2cPolicies = {
    names: {
        signUpSignIn: AUTH_B2C_POLICIES_NAMES_SIGN_UP_SIGN_IN,
    },
    authorities: {
        signUpSignIn: {
            authority: AUTH_B2C_POLICIES_AUTHORITY_SIGN_UP_SIGN_IN,
        },
    },
    authorityDomain: AUTH_B2C_POLICIES_AUTHORITY_DOMAIN,
}

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const msalConfig = {
    auth: {
        clientId: AUTH_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
        authority: b2cPolicies.authorities.signUpSignIn.authority, // Choose SUSI as your default authority.
        knownAuthorities: [b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
        redirectUri: AUTH_REDIRECT_URL, // You must register this URI on Azure Portal/App Registration. Defaults to window.location.origin
        // postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
        // navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
        cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
}

/**
 * Add here the endpoints and scopes when obtaining an access token for protected web APIs. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const protectedResources = {
    app: {
        scopes: {
            read: [AUTH_SCOPE],
            write: [AUTH_SCOPE],
            specSheet: [SPEC_SHEET_SCOPE],
        },
    },
}

const msalInstanceApplication = new PublicClientApplication(msalConfig)

msalInstanceApplication.addEventCallback((event) => {
    const account = (event.payload as { account: AccountInfo | null })?.account

    if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
            event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            event.eventType === EventType.SSO_SILENT_SUCCESS) &&
        account
    ) {
        msalInstanceApplication.setActiveAccount(account)
    }
})

export const msalInstance = msalInstanceApplication

// getAuthToken

export const getToken = async (): Promise<string> => {
    let token = ''
    const account = msalInstance.getActiveAccount()

    if (account) {
        const silentRequest: SilentRequest = {
            scopes: protectedResources.app.scopes.write,
            account: account,
        }
        const updatedToken = await msalInstance
            .acquireTokenSilent(silentRequest)
            .then((res) => {
                return res.accessToken
            })
            .catch((err) => {
                if (err instanceof InteractionRequiredAuthError) {
                    // The user needs to log in interactively, possibly due to an expired session
                    console.error('User session is not available. Initiating logout.')
                    msalInstance.logoutRedirect()
                } else {
                    // Handle other errors...
                    console.error('Error acquiring token silently:', err)
                }
            })

        if (updatedToken) {
            token = updatedToken
        }
    }

    return token
}

export const getSpecSheetToken = async (): Promise<string> => {
    let token = ''
    const account = msalInstance.getActiveAccount()

    if (account) {
        const silentRequest: SilentRequest = {
            scopes: protectedResources.app.scopes.specSheet,
            account: account,
        }

        const updatedToken = await msalInstance
            .acquireTokenSilent(silentRequest)
            .then(async (res) => {
                return res.accessToken
            })
            .catch(async (err) => {
                console.error('Error acquiring token silently:', err)
            })

        if (updatedToken) {
            token = updatedToken
        }
    }

    return token
}

export const getAuthorizationHeader = (): Promise<string> => {
    return getToken().then((token) => `Bearer ${token}`)
}
